// HotpleHomeTab.jsx
import { useEffect, useMemo, useState } from 'react';
import { copyToClipboard, openInstagramMedia } from '../../../util';
import styles from './HotpleHomeTab.module.css';
import AlbumComponent from '../../Main/Album/AlbumComponent';

function ImgSwap({ src, alt, className }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(false), [src]);
  return (
    <div className={className} style={{ position: 'relative' }}>
      {!loaded && <div className={styles.placeholder} />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        decoding="async"
        loading="eager"
        draggable="false"
        style={
          loaded
            ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }
            : { display: 'none' }
        }
      />
    </div>
  );
}

const HotpleHomeTab = ({ hotple, analysis, setHotpleModalPage, setModalProps, marginTop }) => {
  // 앨범 모달 상태
  const [albumOpen, setAlbumOpen] = useState(false);
  const [albumStart, setAlbumStart] = useState(0);

  // hotple 바뀌면 앨범 닫기(잔상 방지)
  useEffect(() => {
    setAlbumOpen(false);
    setAlbumStart(0);
  }, [hotple?.hotple_id]);

  // ESC로 모달 닫기 + 모달 열릴 때 body 스크롤 락
  useEffect(() => {
    if (!albumOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setAlbumOpen(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [albumOpen]);

  const extractMenu = (place) => {
    if (!place || typeof place !== 'object') return [];
    const menu = place.Menu;
    return Array.isArray(menu) ? menu : [];
  };

  const instagram = [analysis?.info?.homepages?.repr, ...(analysis?.info?.homepages?.etc || [])]
    .find(item => item?.type === '인스타그램');

  const instagramHandle = instagram
    ? `${instagram.url.split('?')[0].replace(/https?:\/\/(?:www\.)?instagram\.com\//, '').replace(/\/$/, '')}`
    : null;

  const formatPrice = (price) => {
    if (!price && price !== 0) return '변동';
    if (String(price).includes('~')) {
      return String(price)
        .split('~')
        .map(p => `${Number(p).toLocaleString()}원`)
        .join(' ~ ');
    }
    const n = Number(price);
    return isNaN(n) ? price : `${n.toLocaleString()}원`;
  };

  // 메뉴 정렬/분리 (이미지 있는 메뉴를 앞에)
  const { menusWithImage, menusNoImage } = useMemo(() => {
    const menus = extractMenu(analysis?.info).slice().sort((a, b) => {
      const aHas = a?.images?.length > 0;
      const bHas = b?.images?.length > 0;
      return aHas === bHas ? 0 : aHas ? -1 : 1;
    });
    return {
      menusWithImage: menus.filter(m => m?.images?.length > 0),
      menusNoImage: menus.filter(m => !(m?.images?.length > 0)),
    };
  }, [analysis?.info, hotple?.hotple_id]);

  // ✅ 앨범에는 각 메뉴의 '첫 번째 이미지'만 넣기
  const albumItems = useMemo(() => {
    return menusWithImage
      .map((m) => ({
        src: m.images?.[0],
        title: m.name || "",
        description:
          (m.description || m.desc || "") +
          (m.price !== undefined && m.price !== null
            ? ((m.description || m.desc ? "\n" : "") + `가격: ${formatPrice(m.price)}`)
            : ""),
      }))
      .filter((it) => !!it.src);
  }, [menusWithImage]);

  // 카드 클릭 시 앨범 열기(그 메뉴의 첫 이미지 인덱스로 시작)
  const openAlbumAtMenu = (menuIdx) => {
    setAlbumStart(menuIdx);
    setAlbumOpen(true);
  };

  // ====== AI 분석 섹션 로직 ======
  const getReviewTypeRatios = () => {
    const reviews = analysis?.reviewAnalysis?.analysis || [];
    const total = reviews.length;

    if (total === 0) {
      return {
        positive: 0,
        neutral: 0,
        negative: 0,
        average: 0
      };
    }

    const counts = { positive: 0, neutral: 0, negative: 0 };
    let totalRating = 0;

    reviews.forEach(review => {
      const rating = Number(review?.rating || 0);
      totalRating += rating;

      if (rating >= 4) counts.positive++;
      else if (rating === 3) counts.neutral++;
      else if (rating >= 1 && rating <= 2) counts.negative++;
    });

    return {
      positive: Math.round((counts.positive / total) * 100),
      neutral: Math.round((counts.neutral / total) * 100),
      negative: Math.round((counts.negative / total) * 100),
      average: Math.floor((totalRating / total) * 100) / 100
    };
  };

  const ratios = useMemo(getReviewTypeRatios, [analysis?.reviewAnalysis]);

  const visitorReviewsCount = Number(analysis?.visitorReviewsCount || 0);
  const blogTextsCount = Number(analysis?.blogTextsCount || 0);
  const hasReviewAnalysis = !!analysis?.reviewAnalysis;
  const hasPlaceAnalysis = !!analysis?.placeAnalysis;

  return (
    <div className={styles.bottomSheet}>
      <div
        className={styles.address3}
        onClick={() => { setModalProps({ hotple: { ...hotple, need_verify: false } }); setHotpleModalPage(41); }}
      >
        장소 정보가 틀려요 →
      </div>

      <div className={styles.contentsFrame}>
        {/* 기본 장소/연락처/링크 정보 */}
        <div className={styles.storeInfo}>
          <div className={styles.infoFrame} onClick={()=>copyToClipboard(hotple.address)}>
            <img className={styles.icons} src="images/marker.svg" alt="" />
            <div className={styles.address2}>{hotple.address}</div>
          </div>

          {(analysis?.info?.phone || analysis?.info?.virtualPhone) && (
            <a className={styles.infoFrame} href={`tel:${analysis.info.phone || analysis.info.virtualPhone}`}>
              <img className={styles.icons2} src="images/phone.svg" alt="" />
              <div className={styles.address2}>{analysis.info.phone || analysis.info.virtualPhone}</div>
            </a>
          )}

          {instagram && instagramHandle && (
            <div className={styles.infoFrame} onClick={() => { window.open(instagram.url, '_blank'); }}>
              <img className={styles.icons2} src="images/instagram.svg" alt="" />
              <div className={styles.address2}>{instagramHandle}</div>
            </div>
          )}

          {analysis?.info?.naverBooking?.naverBookingUrl && (
            <div className={styles.infoFrame} onClick={() => { window.open(analysis.info.naverBooking.naverBookingUrl, '_blank'); }}>
              <img className={styles.icons2} src="images/calendar.svg" alt="" />
              <div className={styles.address2}>네이버 예약</div>
            </div>
          )}
        </div>

        {/* 메뉴/가격 */}
        {analysis && (menusWithImage.length > 0 || menusNoImage.length > 0) && (
          <div>
            <div className={styles.title}>
              {(hotple.main_category === '음식점' || hotple.main_category === '주점' || hotple.main_category === '카페디저트') ? '메뉴' : '가격'}
            </div>

            {/* 이미지 있는 메뉴 카드 */}
            {menusWithImage.length > 0 && (
              <div className={styles.menuFrame} key={hotple.hotple_id} onTouchStart={(e)=>{e.stopPropagation();}}>
                <div className={styles.menuItem} style={{ width: '0px' }} />
                {menusWithImage.map((menu, idx) => {
                  const cover = menu.images[0];
                  return (
                    <div
                      key={`${hotple.hotple_id}-img-${idx}`}
                      className={styles.menuItem}
                      onClick={() => openAlbumAtMenu(idx)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openAlbumAtMenu(idx)}
                      style={{ cursor: 'pointer' }}
                    >
                      <ImgSwap src={cover} alt={menu.name} className={styles.menuImage} />
                      <div className={styles.menuName}>{menu.name}</div>
                      <div className={styles.menuPrice}>{formatPrice(menu.price)}</div>
                      {menu.recommend && <div className={styles.eventbadge}>인기</div>}
                    </div>
                  );
                })}
                <div className={styles.menuItem} style={{ width: '0px' }} />
              </div>
            )}

            {/* 이미지 없는 메뉴 */}
            {menusNoImage.length > 0 && (
              <div className={styles.menuFrame2}>
                {menusNoImage.map((menu, idx) => (
                  <div key={`${hotple.hotple_id}-noimg-${idx}`} className={styles.menuItem2}>
                    <div className={styles.menuName2}>· {menu.name}</div>
                    <div className={styles.menuPrice2}>{formatPrice(menu.price)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== AI 분석 섹션 (Home에 합침) ====== */}
        {(hasReviewAnalysis || hasPlaceAnalysis || (hotple?.key_features?.length > 0)) && (
          <div className={styles.contentFrame}>
            {hasReviewAnalysis && (
              <div className={styles.title}>
                <div>AI 분석</div>
                <div className={styles.subtitle}>실제 방문한 사람들의 리뷰들만 모아 분석해봤어요!</div>
              </div>
            )}

            {hasReviewAnalysis && (
              <div className={styles.refParentFrame}>
                {visitorReviewsCount > 0 && (
                  <div className={styles.refFrame}>
                    <img className={styles.iconChecked} alt="" src="images/checked_filled.svg" />
                    <div className={styles.refText}>실제 방문자 리뷰 {visitorReviewsCount}건</div>
                  </div>
                )}
                {blogTextsCount > 0 && (
                  <div className={styles.refFrame}>
                    <img className={styles.iconChecked} alt="" src="images/checked_filled.svg" />
                    <div className={styles.refText}>블로그 리뷰 {blogTextsCount}건</div>
                  </div>
                )}
                <div className={styles.refFrame}>
                  <img className={styles.iconChecked} alt="" src="images/checked_filled.svg" />
                  <div className={styles.refText}>인스타 리뷰 {hotple ? '1' : '0'}건</div>
                </div>
              </div>
            )}

            {hasPlaceAnalysis && (
              <div className={styles.promotionalText}>
                "{analysis.placeAnalysis.promotional_text}"
              </div>
            )}

            {hotple?.key_features?.length > 0 && (
              <div className={styles.analysisFrame}>
                {hotple.key_features.map((item, i) => (
                  <div className={styles.positive_text0} key={`kf-${i}`}>
                    {item}
                  </div>
                ))}
              </div>
            )}

            {hasReviewAnalysis && (
              <div className={styles.reviewframe}>
                <div className={styles.indicatorframe}>
                  {/* 상단 비율 텍스트 */}
                  <div className={styles.indicatortext}>
                    {ratios.positive > 0 && <div className={styles.positivetext}>긍정 {ratios.positive}%</div>}
                    {ratios.neutral  > 0 && <div className={styles.neutraltext}>중립 {ratios.neutral}%</div>}
                    {ratios.negative > 0 && <div className={styles.negativetext}>부정 {ratios.negative}%</div>}
                  </div>

                  {/* 막대 + 평균점수 */}
                  <div className={styles.indicatortext}>
                    <div className={styles.positiveline} style={{ flex: ratios.positive }} />
                    <div className={styles.neutraltext1} style={{ flex: ratios.neutral }} />
                    <div className={styles.negativeline} style={{ flex: ratios.negative }} />
                    <div className={styles.averageText}>AI 리뷰 분석: {ratios.average}점</div>
                  </div>
                </div>

                {/* 긍정 요약 */}
                <div className={styles.positivereviewframe}>
                  <div className={styles.placetitle}>이런 점이 좋았어요</div>
                  {analysis.reviewAnalysis.positive_summary?.length > 0 ? (
                    analysis.reviewAnalysis.positive_summary.map((item, index) => (
                      <div className={styles.positivereviewsub} key={`pos-${index}`}>
                        · {item.summary}
                      </div>
                    ))
                  ) : (
                    <div className={styles.positivereviewsub}>긍정 리뷰가 없습니다.</div>
                  )}
                </div>

                {/* 부정 요약 */}
                <div className={styles.negativereviewframe}>
                  <div className={styles.placetitle2}>이런 점은 조금 아쉬웠어요</div>
                  {analysis.reviewAnalysis.negative_summary?.length > 0 ? (
                    analysis.reviewAnalysis.negative_summary.map((item, index) => (
                      <div className={styles.positivereviewsub} key={`neg-${index}`}>
                        · {item.summary}
                      </div>
                    ))
                  ) : (
                    <div className={styles.positivereviewsub}>부정 리뷰가 없습니다 🙂</div>
                  )}
                </div>

                {/* 장소 설명 */}
                {hasPlaceAnalysis && (
                  <div className={styles.placeresultframe0}>
                    <div className={styles.placeresulttext}>
                      {String(analysis.placeAnalysis.description || '').replaceAll('**', '')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 원본 게시글 본문 */}
        <div className={styles.frame}>
          <div className={styles.title}>
            <div>원본 게시글 본문</div>
            <div className={styles.author} onClick={() => openInstagramMedia(hotple.media_url)}>
              @{hotple.media_creator}
            </div>
          </div>
          <div className={styles.placeresultframe}>
            <div className={styles.placeresulttext}>{hotple.media_caption}</div>
          </div>
        </div>
      </div>

      {/* 앨범 모달: 각 메뉴의 '대표 한 장'만 좌우로 넘김 */}
      <AlbumComponent
        open={albumOpen}
        items={albumItems}
        startIndex={albumStart}
        preloadRadius={2}
        onIndexChange={() => {}}
        onClose={() => setAlbumOpen(false)}
        marginTop={marginTop}
      />
    </div>
  );
};

export default HotpleHomeTab;
